/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { isArray, isObject, get } from 'lodash'

export interface TransformFunc {
  (data: any): any
}

const applyTransform = (data: unknown, transform: TransformFunc | undefined) => {
  if (!transform) return data

  if (isArray(data)) {
    return data.map((x) => transform(x))
  }
  if (data) {
    return transform(data)
  }

  return data
}

export interface GetSingleFromApi<T extends object> {
  (apiClient: AxiosInstance, ids: Record<string, string | number>): Promise<T>
}

export interface GetListFromApi<T extends object> {
  (apiClient: AxiosInstance, filters?: Record<string, string | number | string[] | number[]>): Promise<T[]>
}

export interface SaveToApi<TInput extends object, TOutput extends object = TInput> {
  (apiClient: AxiosInstance, data: Partial<TInput>): Promise<TOutput>
}

const serializeParameter = (params: Record<string, string | number | string[] | number[]> | undefined) => {
  const searchParams = new URLSearchParams()
  if (params)
    Object.keys(params).forEach((key) => {
      const v = params[key]
      if (v.toString()) searchParams.append(key, v.toString())
    })
  return searchParams.toString()
}

export function buildUrlWithIds(baseUrl: string, ids: Record<string, unknown>): string {
  let newUrl = baseUrl
  const paramNames = baseUrl.match(/[^{}]+(?=})/g)
  paramNames?.forEach((name) => {
    const value = get(ids, name)
    if (value === undefined || value === null || value === '') {
      throw new Error(`parameter ${name} for url ${baseUrl} was given a missing value.`)
    } else if (isObject(ids[name])) {
      throw new Error(`parameter ${name} for url ${baseUrl} was given an object instead of a direct value.`)
    } else {
      newUrl = newUrl.replace(`{${name}}`, (value as object).toString())
    }
  })

  return newUrl
}

export const makeGetSingleFromApi = <T extends object>(
  baseUrl: string,
  fromApi?: TransformFunc | undefined,
  hostname?: string,
): GetSingleFromApi<T> => {
  return async (apiClient: AxiosInstance, ids: Record<string, string | number>): Promise<T> => {
    const payload: AxiosRequestConfig<T> = { method: 'GET', url: buildUrlWithIds(baseUrl, ids) }

    if (hostname) payload.baseURL = hostname

    const response = await apiClient.request(payload)
    const ret = applyTransform(response.data, fromApi)
    return ret as T
  }
}

export const makeGetListFromApi = <T extends object>(
  baseUrl: string,
  fromApi?: TransformFunc | undefined,
): GetListFromApi<T> => {
  return async (
    apiClient: AxiosInstance,
    filters?: Record<string, string | number | string[] | number[]>,
  ): Promise<T[]> => {
    const payload: AxiosRequestConfig<T> = { method: 'GET', url: baseUrl.concat(`?${serializeParameter(filters)}`) }
    const response = await apiClient.request(payload)
    const ret = applyTransform(response.data, fromApi)
    return ret as T[]
  }
}

export const makeCreate = <TInput extends object, TOutput extends object = TInput>(
  baseUrl: string,
  toApi?: TransformFunc | undefined,
  fromApi?: TransformFunc | undefined,
): SaveToApi<TInput, TOutput> => {
  return async (apiClient: AxiosInstance, data: Partial<TInput>): Promise<TOutput> => {
    const payload: AxiosRequestConfig<TInput[]> = {
      method: 'POST',
      url: buildUrlWithIds(baseUrl, data),
      data: applyTransform(data, toApi),
    }
    const response = await apiClient.request(payload)
    const ret = applyTransform(response.data, fromApi)
    return ret as TOutput
  }
}

export const makeUpdate = <TInput extends object, TOutput extends object = TInput>(
  baseUrl: string,
  toApi?: TransformFunc | undefined,
  fromApi?: TransformFunc | undefined,
): SaveToApi<TInput, TOutput> => {
  return async (apiClient: AxiosInstance, data: Partial<TInput>): Promise<TOutput> => {
    const payload: AxiosRequestConfig<TInput> = {
      url: buildUrlWithIds(baseUrl, data),
      method: 'PUT',
      data: applyTransform(data, toApi),
    }
    const response = await apiClient.request(payload)
    const ret = applyTransform(response.data, fromApi)
    return ret as TOutput
  }
}

export const makeDeleteWithReturn = <TInput extends object, TOutput extends object = TInput>(
  baseUrl: string,
  toApi?: TransformFunc | undefined,
  fromApi?: TransformFunc | undefined,
): SaveToApi<TInput, TOutput> => {
  return async (apiClient: AxiosInstance, data: Partial<TInput>): Promise<TOutput> => {
    const payload: AxiosRequestConfig<TInput> = {
      url: buildUrlWithIds(baseUrl, data),
      method: 'DELETE',
      data: applyTransform(data, toApi),
    }
    const response = await apiClient.request(payload)
    const ret = applyTransform(response.data, fromApi)
    return ret as TOutput
  }
}
